import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Loader2, Plug, CheckCircle2, XCircle, Save } from "lucide-react";
import { toast } from "sonner";

export default function AiStatusPanel() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [testing, setTesting] = useState(false);
  const [limit, setLimit] = useState("");

  const load = () => {
    api.get("/marketing/ai/status").then((r) => setStatus(r.data));
    api.get("/marketing/ai/settings").then((r) => { setSettings(r.data); setLimit(String(r.data.monthly_job_limit)); });
  };
  useEffect(() => { load(); }, []);

  const test = async () => {
    setTesting(true);
    try { const r = await api.post("/marketing/ai/test-connection"); toast.success(`Connected · ${r.data.model}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Connection failed"); load(); }
    finally { setTesting(false); }
  };

  const saveLimit = async () => {
    try { await api.put("/marketing/ai/settings", { monthly_job_limit: Number(limit) }); toast.success("Limit updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (!status || !settings) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="ai-status-panel">
      <div className="flex items-center gap-2">
        <Plug className="h-4 w-4 text-[#581C87]" />
        <h3 className="font-heading font-bold">AI Connection Status</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
        <div><p className="text-xs uppercase tracking-widest text-gray-400">Provider</p><p className="text-gray-800">{status.provider === "openai" ? "OpenAI" : status.provider}</p></div>
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-400">Status</p>
          <p className="flex items-center gap-1.5" data-testid="ai-configured-state">
            {status.configured ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Configured</> : <><XCircle className="h-4 w-4 text-gray-400" /> Not Configured</>}
          </p>
        </div>
        <div><p className="text-xs uppercase tracking-widest text-gray-400">Model</p><p className="text-gray-800">{status.model || "—"}</p></div>
        <div><p className="text-xs uppercase tracking-widest text-gray-400">Last Success</p><p className="text-gray-800">{status.last_success_at ? status.last_success_at.slice(0, 16).replace("T", " ") : "—"}</p></div>
      </div>
      {status.last_error && (
        <p className="mt-3 text-xs text-red-600" data-testid="ai-last-error">Last error: {status.last_error.code} — {status.last_error.message}</p>
      )}
      {!status.configured && (
        <p className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Set <code className="font-mono">OPENAI_API_KEY</code> in the backend environment to enable generation. The key is never displayed.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={test} disabled={testing} variant="outline" className="border-gray-200 hover:border-[#581C87] hover:text-[#581C87]" data-testid="test-connection-button">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test Connection"}
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <span className={`text-xs ${settings.warn ? "text-amber-600 font-semibold" : "text-gray-500"}`} data-testid="ai-usage">
            Usage: {settings.usage_count}/{settings.monthly_job_limit} jobs this month{settings.warn ? " · 80%+ used" : ""}
          </span>
          <Input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="w-24 focus-visible:ring-[#581C87]" data-testid="monthly-limit-input" />
          <Button onClick={saveLimit} size="sm" className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="save-limit-button"><Save className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
